import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Loader from "../Common/Loader";
import { getBranchesAction } from "../../../store/actions/BranchAction";
import {
  getStockTransactionsAction,
  formatDateWithTime,
} from "../../../store/actions/stockTransactionActions";

const StockTransactions = () => {
  const dispatch = useDispatch();

  const branches = useSelector((state) => state.branches?.branches || []);
  const branchesLoading = useSelector(
    (state) => state.branches?.loading || false
  );

  const {
    transactions = [],
    num_rows = 0,
    loading: transactionsLoading = false,
    error,
  } = useSelector((state) => state.stockTransaction || {});

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [searchQueries, setSearchQueries] = useState({
    created_at: "",
    medicine_name: "",
    transaction_type: "",
    qty: "",
    balance_qty: "",
  });

  const rowsPerPage = 10;

  const isFirstLoad = useRef(true);
  const searchTimeout = useRef(null);

  // ================= FETCH BRANCHES =================
  useEffect(() => {
    dispatch(getBranchesAction(false));
  }, [dispatch]);

  // ================= SET DEFAULT BRANCH =================
  useEffect(() => {
    if (branches?.length > 0 && !selectedBranch) {
      const firstBranch = branches[0];
      setSelectedBranch({
        value: firstBranch.branch_id,
        label: firstBranch.branch_name,
      });
    }
  }, [branches, selectedBranch]);

  // ================= FETCH TRANSACTIONS =================
  useEffect(() => {
    if (selectedBranch) {
      fetchTransactions();
    }
  }, [currentPage, selectedBranch]);

  const fetchTransactions = async () => {
    try {
      await dispatch(
        getStockTransactionsAction(
          true,
          currentPage,
          activeSearchField,
          activeSearchKeyword,
          sortField,
          sortOrder,
          selectedBranch?.value || ""
        )
      );
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
    } finally {
      isFirstLoad.current = false;
    }
  };

  // ================= SEARCH =================
  const handleSearch = (e, field) => {
    const keyword = e.target.value;

    const clearedQueries = {
      created_at: "",
      medicine_name: "",
      transaction_type: "",
      qty: "",
      balance_qty: "",
    };

    const updatedQueries = {
      ...clearedQueries,
      [field]: keyword,
    };

    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    if (keyword.trim()) {
      setActiveSearchField(field);
      setActiveSearchKeyword(keyword);
    } else {
      setActiveSearchField("");
      setActiveSearchKeyword("");
    }

    // Debounce API call
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      dispatch(
        getStockTransactionsAction(
          true,
          0,
          keyword.trim() ? field : "",
          keyword.trim() ? keyword : "",
          sortField,
          sortOrder,
          selectedBranch?.value || ""
        )
      );
    }, 400);
  };

  // ================= SORT =================
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);

    dispatch(
      getStockTransactionsAction(
        true,
        0,
        activeSearchField,
        activeSearchKeyword,
        field,
        order,
        selectedBranch?.value || ""
      )
    );
  };

  // ================= BRANCH CHANGE =================
  const handleBranchChange = (selected) => {
    setSelectedBranch(selected);
    setCurrentPage(0);

    setSearchQueries({
      created_at: "",
      medicine_name: "",
      transaction_type: "",
      qty: "",
      balance_qty: "",
    });

    setActiveSearchField("");
    setActiveSearchKeyword("");
  };

  // ================= PAGINATION =================
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(num_rows / rowsPerPage);
  const startEntry = num_rows === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endEntry = Math.min((currentPage + 1) * rowsPerPage, num_rows);

  const branchOptions = branches.map((b) => ({
    value: b.branch_id,
    label: b.branch_name,
  }));

  const getTransactionBadge = (type) => {
    if (type?.toLowerCase() === "inward") {
      return <span className="badge bg-success">Inward</span>;
    }
    if (type?.toLowerCase() === "outward") {
      return <span className="badge bg-warning text-dark">Outward</span>;
    }
    return <span className="badge bg-secondary">{type || "-"}</span>;
  };

  const isLoading = branchesLoading || (transactionsLoading && isFirstLoad.current);

  return (
    <>
      <div className="row mb-3 align-items-end">
        <div className="col-lg-6">
          <h4 className="mb-0">
            <i className="fas fa-exchange-alt me-2"></i>
            Inward/Outward Stock Transactions
          </h4>
        </div>

        <div className="col-lg-6">
          <div style={{ maxWidth: "300px", marginLeft: "auto" }}>
            <Select
              options={branchOptions}
              value={selectedBranch}
              onChange={handleBranchChange}
              placeholder="Select Branch"
              isClearable
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="alert alert-danger mb-3">{error}</div>}

          {isLoading ? (
            <div className="text-center my-5">
              <Loader size="lg" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped mb-4" style={{ minWidth: "800px" }}>
                  <thead className="table-light">
                    <tr>
                      <th
                        style={{ cursor: "pointer", whiteSpace: "nowrap", width: "auto" }}
                        onClick={() => handleSort("created_at")}
                      >
                        Date{" "}
                        {sortField === "created_at" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.created_at}
                          onChange={(e) => handleSearch(e, "created_at")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Date"
                        />
                      </th>

                      <th style={{ minWidth: "600px" }}>
                        Medicine Details
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.medicine_name}
                          onChange={(e) => handleSearch(e, "medicine_name")}
                          placeholder="Search Medicine"
                        />
                      </th>

                      <th
                        style={{ cursor: "pointer", minWidth: "100px", textAlign: "center" }}
                        onClick={() => handleSort("transaction_type")}
                      >
                        Transaction{" "}
                        {sortField === "transaction_type" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.transaction_type}
                          onChange={(e) => handleSearch(e, "transaction_type")}
                          placeholder="Search Type"
                        />
                      </th>

                      <th
                        style={{ cursor: "pointer", minWidth: "80px", textAlign: "center" }}
                        onClick={() => handleSort("qty")}
                      >
                        Qty{" "}
                        {sortField === "qty" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          type="number"
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.qty}
                          onChange={(e) => handleSearch(e, "qty")}
                          placeholder="Search Qty"
                        />
                      </th>

                      <th
                        style={{ cursor: "pointer", minWidth: "100px", textAlign: "center" }}
                        onClick={() => handleSort("balance_qty")}
                      >
                        Balance Qty{" "}
                        {sortField === "balance_qty" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          type="number"
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.balance_qty}
                          onChange={(e) => handleSearch(e, "balance_qty")}
                          placeholder="Search Balance"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {transactions?.length > 0 ? (
                      transactions.map((item) => (
                        <tr key={item.id}>
                          <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                            {formatDateWithTime(item.created_at)}
                          </td>
                          <td style={{ verticalAlign: "middle", wordBreak: "break-word" }}>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                              <strong>{item.medicine_name}</strong>
                              {item.batch_no && (
                                <span className="small text-muted">
                                  Batch: {item.batch_no}
                                </span>
                              )}
                              {item.carton_id && (
                                <span className="small text-muted">
                                  Carton: {item.carton_id}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="fw-bold" style={{ verticalAlign: "middle", textAlign: "center" }}>
                            {getTransactionBadge(item.transaction_type)}
                          </td>
                          <td className="fw-bold" style={{ verticalAlign: "middle", textAlign: "center" }}>
                            {item.qty}
                          </td>
                          <td className="fw-bold" style={{ verticalAlign: "middle", textAlign: "center" }}>
                            {item.balance_qty}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-5">
                          No transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {num_rows > 0 && (
                <div
                  className="pagination-wrapper"
                  style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "20px",
                    flexWrap: "wrap",
                    flexDirection: "column",
                  }}
                >
                  <div>
                    Showing {startEntry} to {endEntry} of {num_rows} entries
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                      disabled={currentPage === 0}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor: "#fff",
                        color: "#000",
                        cursor: currentPage === 0 ? "not-allowed" : "pointer",
                        opacity: currentPage === 0 ? 0.5 : 1,
                      }}
                    >
                      &lt;
                    </button>

                    {(() => {
                      const pages = [];
                      const maxPagesToShow = 5;
                      const startPage = Math.max(
                        0,
                        Math.min(currentPage - 2, totalPages - maxPagesToShow)
                      );
                      const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                      if (startPage > 0) {
                        pages.push(
                          <button
                            key={0}
                            onClick={() => handlePageChange(0)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              backgroundColor: "#fff",
                              color: "#000",
                              cursor: "pointer",
                            }}
                          >
                            1
                          </button>
                        );
                        if (startPage > 1) {
                          pages.push(
                            <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                              ...
                            </span>
                          );
                        }
                      }

                      for (let i = startPage; i < endPage; i++) {
                        const isActive = currentPage === i;
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              backgroundColor: isActive ? "#007bff" : "#fff",
                              color: isActive ? "#fff" : "#000",
                              cursor: "pointer",
                            }}
                          >
                            {i + 1}
                          </button>
                        );
                      }

                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages - 1}
                            onClick={() => handlePageChange(totalPages - 1)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              backgroundColor: "#fff",
                              color: "#000",
                              cursor: "pointer",
                            }}
                          >
                            {totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}

                    <button
                      onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                      disabled={currentPage === totalPages - 1}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor: "#fff",
                        color: "#000",
                        cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                        opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                      }}
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StockTransactions;